import {
  ForwardedRef,
  forwardRef,
  memo,
  ReactElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import { Button, DatePicker, Flex, Space, Switch, Tooltip, Typography } from 'antd';
import type { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { CopyOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import { NoUndefinedRangeValueType, RangeValueType } from 'rc-picker/lib/PickerInput/RangePicker';
import { Guard, useGuard } from '@ohpen/react-authz';
import { Table, TableComponent, TableProps, CrudMode, TableColumns, InfoTooltip, getAllTableColumns, BoxedSwitch } from '@ohpen/enso-ui';

import { ActionEntityType } from '@pmp/api';
import { useEntityExternalFilters, usePmpFetch, useTableView } from '@pmp/hooks';
import { CrudTable, ProductCrudData } from '@pmp/components';
import { ChangeRequestsStateCrudTable } from '@pmp/pages';
import { ProductData } from '@pmp/types';
import { useExportTemplatesStore } from '@pmp/stores';

import { productsColumns } from '../tableConfig';
import { ProductsListProps, ProductsListComponent, ViewState, ProductsListFilters } from '../interfaces';
import {
  filterActiveToday,
  flattenProductAdditionalFields,
  productsRequestYears,
  productsToProductsCrud,
  removeReplacesCode,
} from '../utils';
import { ProductView } from './ProductView';

const { RangePicker } = DatePicker;
const { Text } = Typography;

const entityType = ActionEntityType.PRODUCTS;
const rowKey = 'code';

const getDefaultFilters = (): ProductsListFilters => ({
  dates: [dayjs().format('YYYY'), dayjs().add(1, 'year').format('YYYY')],
  activeToday: true,
});

const ProductsListBase = (
  { valueLists, additionalFields, fieldsLoading }: ProductsListProps,
  externalRef: ForwardedRef<ProductsListComponent>,
): ReactElement => {
  const tableRef = useRef<TableComponent | null>(null);
  const [viewData, setViewData] = useState<ViewState>({
    isOpen: false,
    record: null,
  });
  const [crudData, setCrudData] = useState<ChangeRequestsStateCrudTable>({
    isOpen: false,
    isLoading: false,
    data: null,
    originalData: null, // TODO check if this is needed
    mode: null,
    entityType,
  });
  const { isLoadingExportTemplates, getExportTemplates } = useExportTemplatesStore();
  const { tableViews, saveTableView, deleteTableView } = useTableView(ActionEntityType.PRODUCTS);
  const [productsLoading, , productsResult, , getProducts] = usePmpFetch('productsGetAllProducts');
  const [activeRowId, setActiveRowId] = useState<string | null>(null);
  const defaultFilters = useMemo(getDefaultFilters, []);
  const [{ dates, activeToday }, setFilters] = useEntityExternalFilters<ProductsListFilters>(entityType, defaultFilters);
  const [selectedRows, setSelectedRows] = useState<ProductData[]>([]);
  const { allowed: showRowSelection } = useGuard({
    permissions: ['products:update'],
    flags: ['enableProductsCrud'],
  });
  const { t } = useTranslation();

  const refreshTable = useCallback(() => {
    getProducts({ query: { years: productsRequestYears(dates) } });
  }, [dates, getProducts]);

  useImperativeHandle(externalRef, () => {
    return { refreshTable };
  });

  useEffect(() => {
    refreshTable();
  }, [refreshTable]);

  useEffect(() => {
    tableRef.current?.resetFilteredData?.();
  }, [activeToday]);

  const columns: TableColumns<ProductData> = useMemo(() => {
    return getAllTableColumns(productsColumns, additionalFields);
  }, [additionalFields]);

  const resetSelectedRows = useCallback(() => {
    tableRef.current?.resetSelectedRows();
  }, []);

  const onView = useCallback((record: ProductData) => {
    setActiveRowId(record[rowKey] ?? null);
    setViewData({ isOpen: true, record });
  }, []);

  const onCreate = useCallback(() => {
    setCrudData({ isOpen: true, data: null, originalData: null, mode: CrudMode.CREATE, entityType });
  }, []);

  const onEdit = useCallback((record: ProductData) => {
    const data: ProductCrudData[] = productsToProductsCrud([record]);

    setActiveRowId(record[rowKey] ?? null);
    setCrudData({ isOpen: true, data, originalData: data, mode: CrudMode.EDIT, entityType });
  }, []);

  const onClone = useCallback((record: ProductData) => {
    const data: ProductCrudData[] = removeReplacesCode(productsToProductsCrud([record]));

    setActiveRowId(record[rowKey] ?? null);
    setCrudData({ isOpen: true, data, originalData: data, mode: CrudMode.CLONE, entityType });
  }, []);

  const onBulkEdit = useCallback((selectedRows: ProductData[]) => {
    const data: ProductCrudData[] = productsToProductsCrud(selectedRows);

    setCrudData({ isOpen: true, data, originalData: data, mode: CrudMode.BULK_EDIT, entityType });
  }, []);

  const onBulkClone = useCallback((selectedRows: ProductData[]) => {
    const data: ProductCrudData[] = removeReplacesCode(productsToProductsCrud(selectedRows));

    setCrudData({ isOpen: true, data, originalData: data, mode: CrudMode.BULK_CLONE, entityType });
  }, []);

  const onCloseCrud = useCallback(() => {
    setCrudData({ isOpen: false, data: null, originalData: null, mode: null, entityType });
  }, []);

  const onCloseView = useCallback(() => {
    setViewData({ isOpen: false, record: null });
  }, []);

  const onChangeDate = useCallback((values: NoUndefinedRangeValueType<Dayjs> | null, dateStrings: [string, string]) => {
    setFilters(prev => ({ ...prev, dates: [dayjs(values?.[0]).format('YYYY'), dayjs(values?.[1]).format('YYYY')] }));
  }, [setFilters]);

  const onChangeActiveToday = useCallback((checked: boolean) => {
    setFilters(prev => ({ ...prev, activeToday: checked }));
  }, [setFilters]);

  const onChangeSelectedRows = useCallback((selectedRows: ProductData[]) => {
    setSelectedRows(selectedRows);
  }, []);

  const getTemplates = useCallback(() => getExportTemplates(ActionEntityType.PRODUCTS), [getExportTemplates]);

  const getRowActions: (record: ProductData) => ReactElement[] = useCallback(
    record => {
      const editTitle = record.lockedByActionId ? (
        <>
          {t('global.buttons.editLockedByChangeRequest')}
          <Link to={`/change-requests?search-change-requests=${encodeURIComponent(record.lockedByActionId)}`}>
            {record.lockedByActionId}
          </Link>
        </>
      ) : (
        t('global.buttons.edit')
      );
      const rowActions = [
        <Tooltip key="view" title={t('global.buttons.view')} placement="top">
          <Button type="text" icon={<EyeOutlined />} onClick={() => onView(record)} />
        </Tooltip>,
        <Guard key="edit" permissions={['products:update']} flags={['enableProductsCrud']}>
          <Tooltip key="edit" title={editTitle} placement="top">
            <Button
              type="text"
              aria-label="Edit Product"
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
              disabled={!!record.lockedByActionId}
            />
          </Tooltip>
          <Tooltip key="clone" title={t('global.buttons.clone')} placement="top">
            <Button type="text" aria-label="Edit Product" icon={<CopyOutlined />} onClick={() => onClone(record)} />
          </Tooltip>
        </Guard>,
      ];
      return rowActions;
    },
    [onClone, onEdit, onView, t],
  );

  const disabledBulkEdit: boolean = useMemo(() => selectedRows?.some(record => !!record.lockedByActionId) ?? false, [selectedRows]);

  const isLoading = useMemo(() => fieldsLoading || productsLoading, [fieldsLoading, productsLoading]);

  const flattenProducts: ProductData[] = useMemo(
    () => (productsResult ? flattenProductAdditionalFields(productsResult) : []),
    [productsResult],
  );

  const dataSource: ProductData[] = useMemo(
    () => (activeToday ? filterActiveToday(flattenProducts) : flattenProducts),
    [activeToday, flattenProducts],
  );

  const tableProps: TableProps<ActionEntityType, ProductData> = useMemo(
    () => ({
      id: entityType,
      itemsLabel: t('pmp.products.labels.products'),
      ref: tableRef,
      dataSource,
      columns,
      rowKey,
      pagination: true,
      scroll: { x: 'max-content', y: '100%' },
      valueLists,
      loading: isLoading,
      activeRowId,
      initialGlobalSearchColumn: rowKey,
      getRowActions,
      showRowSelection,
      showViews: true,
      tableViews,
      saveTableView,
      deleteTableView,
      onChangeSelectedRows,
      additionalFields: true,
    }),
    [
      t,
      dataSource,
      columns,
      valueLists,
      isLoading,
      activeRowId,
      getRowActions,
      showRowSelection,
      tableViews,
      saveTableView,
      deleteTableView,
      onChangeSelectedRows,
    ],
  );

  const datePickerValues: RangeValueType<Dayjs> | null | undefined = useMemo(
    () => [dayjs(dates?.[0] ?? undefined), dayjs(dates?.[1] ?? undefined)],
    [dates],
  );

  return (
    <>
      <Table {...tableProps}>
        <>
          <Table.ActionsBar>
            <Flex justify="space-between" gap="middle">
              <Space size="middle">
                <Table.GlobalSearch />
                <RangePicker value={datePickerValues} onChange={onChangeDate} picker="year" format="YYYY" />
                <InfoTooltip text={t('global.labels.infoTooltipText')} />
                <BoxedSwitch gap="middle" align="center">
                  <Text>{t('pmp.products.labels.activeToday')}</Text>
                  <Switch value={activeToday} onChange={onChangeActiveToday} />
                </BoxedSwitch>
              </Space>
              <Space size="middle">
                <Table.ResetFiltersButton />
                <Table.SelectColumnsButton />
                <Guard flags={['enableExports']}>
                  <Table.ExportButton
                    fileName={`Products available at ${dates ?? dayjs(new Date()).format('YYYY')} on ${dayjs(new Date()).format('YYYY-MM-DD')}`}
                    sheetName="Products"
                    disabled={isLoading}
                    loadingTemplates={isLoadingExportTemplates}
                    allowSearch={true}
                    getTemplates={getTemplates}
                    modalDefaults={{ columnScope: 'template' }}
                    editableFileName
                  />
                </Guard>
                <Guard permissions={['products:create']} flags={['enableProductsCrud']}>
                  <Table.CreateButton onClick={onCreate} />
                </Guard>
              </Space>
            </Flex>
          </Table.ActionsBar>
          <Table.BulkBar xItemsSelectedLabel="pmp.products.labels.xProductsSelected">
            <Space size="middle">
              <Table.BulkCloneButton onClick={onBulkClone} />
              <Table.BulkEditButton
                onClick={onBulkEdit}
                disabled={disabledBulkEdit}
                tooltip={disabledBulkEdit ? t('global.labels.bulkEditLockedByChangeRequest') : undefined}
              />
              <Guard flags={['enableExports']}>
                <Table.ExportButton
                  fileName={`Products available at ${dates ?? dayjs(new Date()).format('YYYY')} on ${dayjs(new Date()).format('YYYY-MM-DD')}`}
                  sheetName="Products"
                  disabled={isLoading}
                  loadingTemplates={isLoadingExportTemplates}
                  allowSearch={true}
                  getTemplates={getTemplates}
                  modalDefaults={{ columnScope: 'template' }}
                  editableFileName
                  exportView
                />
              </Guard>
            </Space>
          </Table.BulkBar>
        </>
      </Table>
      {viewData.isOpen && (
        <ProductView record={viewData.record} valueLists={valueLists} additionalFields={additionalFields} onClose={onCloseView} />
      )}
      {crudData.isOpen && (
        <CrudTable
          entityType={ActionEntityType.PRODUCTS}
          data={crudData.data}
          columns={productsColumns}
          mode={crudData.mode}
          additionalFields={additionalFields}
          valueLists={valueLists}
          setActiveRowId={setActiveRowId}
          refreshTable={refreshTable}
          resetSelectedRows={resetSelectedRows}
          onClose={onCloseCrud}
        />
      )}
    </>
  );
};

export const ProductsList = memo(forwardRef(ProductsListBase));
